import { Fragment } from "preact";
import { TargetedEvent } from "preact/compat";
import { useSignal } from "@preact/signals";
import type { BaseTableRow, TableProps } from "./interfaces";
import ArrowIcon from "~/assets/arrow.svg";
import cn from "classnames";

const Table = <Row extends BaseTableRow, Cell extends string>({
    cells = [],
    rows = [],
    header,
    className,
    renderSummary,
    onRowClick,
    activeRows,
    expand: Expand,
    renderers = {},
    sizes,
    ...rest
}: TableProps<Row, Cell>) => {
    const expanded = useSignal<Row[]>([]);

    const isClickable = Boolean(onRowClick);
    const isExpandable = Boolean(Expand);

    const getOnRowClick = (row: Row) => (event: TargetedEvent) => {
        if ((event.target as HTMLElement).closest("button")) {
            return;
        }

        onRowClick?.(row);
    };

    const getOnRowKey = (row: Row) => (event: KeyboardEvent) => {
        if (event.key === "Enter") {
            onRowClick?.(row);
        }
    };

    const toggleExpand = (row: Row) => {
        if (expanded.value.includes(row)) {
            expanded.value = expanded.value.filter((item) => item !== row);
        } else {
            expanded.value = expanded.value.concat(row);
        }
    };

    const renderCell = (cell: Cell, row: Row, index: number) => {
        const renderer = renderers[cell];

        if (renderer) {
            return renderer(row, index);
        }

        if (cell in row) {
            return <>{String(row[cell as keyof Row])}</>;
        }

        return <>-</>;
    };

    return (
        <table
            className={cn(
                "border-spacing-0 border-separate pt-4",
                className && String(className),
            )}
            {...rest}
        >
            <thead className="-translate-y-4">
                <tr className="sticky top-0 z-20 bg-sky-200/50 font-bold backdrop-blur-md">
                    {isExpandable && <th className="p-2" />}
                    {cells.map((cell) => (
                        <th
                            width={sizes?.[cell]}
                            className="p-2 text-left first:border-l first:rounded-l-2xl last:border-r last:rounded-r-2xl"
                            key={cell}
                        >
                            {header?.[cell] || cell}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, index) => (
                    <Fragment key={row.id}>
                        <tr
                            tabIndex={isClickable ? 0 : undefined}
                            onKeyDown={
                                isClickable ? getOnRowKey(row) : undefined
                            }
                            className={cn(
                                "outline-none focus:bg-sky-100",
                                isClickable && "cursor-pointer",
                                activeRows?.includes(row)
                                    ? "bg-sky-100"
                                    : expanded.value.includes(row) &&
                                          "bg-gray-100 hover:bg-sky-100",
                                !activeRows?.includes(row) &&
                                    isClickable &&
                                    "hover:bg-sky-100",
                            )}
                            onClick={getOnRowClick(row)}
                        >
                            {isExpandable && (
                                <td
                                    className="px-2 p-1/2 hover:bg-sky-200"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        toggleExpand(row);
                                    }}
                                >
                                    <ArrowIcon
                                        className={cn(
                                            "transition duration-100",
                                            !expanded.value.includes(row) &&
                                                "-rotate-90",
                                        )}
                                    />
                                </td>
                            )}
                            {cells.map((cell) => (
                                <td className="px-2 py-1" key={cell}>
                                    {renderCell(cell, row, index)}
                                </td>
                            ))}
                        </tr>
                        {expanded.value.includes(row) && Expand && (
                            <tr
                                className={cn(
                                    activeRows?.includes(row)
                                        ? "bg-sky-200"
                                        : "bg-gray-100",
                                )}
                            >
                                <td colSpan={cells.length + 1}>
                                    <Expand row={row} />
                                </td>
                            </tr>
                        )}
                    </Fragment>
                ))}
                {renderSummary && (
                    <tr className="sticky bottom-0 z-20 font-bold bg-white">
                        {cells.map((cell) => (
                            <td
                                key={cell}
                                className="p-2 border-t border-gray-300"
                            >
                                {renderSummary(cell, rows)}
                            </td>
                        ))}
                    </tr>
                )}
            </tbody>
        </table>
    );
};

export default Table;
